import { Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Loader } from '@hopskipdrive/react-components';

import { useAuthQuery } from '../../auth/hooks/useAuthQuery';
import { PageLayout } from '../../shared/components';
import NotFound from '../../shared/pages/NotFound';
import ProtectedRoute from '../ProtectedRoute';
import { PrivateRouteConfig, PublicRouteConfig } from '../types';
import { useDynamicTitle } from '../useDynamicTitle';

type RoutesRendererProps = {
  privateRoutes: PrivateRouteConfig[];
  publicRoutes: PublicRouteConfig[];
  onsiteStaffRoutes: PrivateRouteConfig[];
};

const ConfiguredRouteElement = ({ config }: { config: PrivateRouteConfig }) => {
  const { component: Component, allowedAction, allowedSubject, nestedRoutes, selfGuarded } = config;
  const isPolicyFreeGroup =
    selfGuarded === true &&
    Boolean(nestedRoutes?.length) &&
    allowedAction === undefined &&
    allowedSubject === undefined;

  return (
    <Suspense fallback={<Loader />}>
      {isPolicyFreeGroup ? (
        <Component />
      ) : (
        <ProtectedRoute {...{ allowedAction, allowedSubject }}>
          <Component />
        </ProtectedRoute>
      )}
    </Suspense>
  );
};

const renderPrivateRoutes = (routeConfigs: PrivateRouteConfig[]): JSX.Element[] =>
  routeConfigs.map((config) => {
    const { path, index, caseSensitive, id, nestedRoutes } = config;
    const element = <ConfiguredRouteElement config={config} />;

    return index ? (
      <Route key={id ?? 'index'} index element={element} caseSensitive={caseSensitive} id={id} />
    ) : (
      <Route key={id ?? `path:${path ?? ''}`} path={path} element={element} caseSensitive={caseSensitive} id={id}>
        {nestedRoutes && renderPrivateRoutes(nestedRoutes)}
      </Route>
    );
  });

function RoutesRenderer({ privateRoutes, publicRoutes, onsiteStaffRoutes }: RoutesRendererProps): JSX.Element {
  const { user, isOnsiteStaffUser } = useAuthQuery();

  const defaultRoute = isOnsiteStaffUser ? '/rides/daily_queue' : '/rides';
  const filteredRoutes = isOnsiteStaffUser ? onsiteStaffRoutes : privateRoutes;

  useDynamicTitle({ privateRoutes: filteredRoutes, publicRoutes });

  return (
    <Routes>
      <Route element={<PageLayout />}>
        {renderPrivateRoutes(filteredRoutes)}

        <Route path="/" element={<Navigate to={defaultRoute} />} />
        {user && <Route path="/login" element={<Navigate to="/" />} />}
        {publicRoutes.map(({ path, component: Component }) => (
          <Route key={path} path={path} element={<Component />} />
        ))}
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

export default RoutesRenderer;
